import { supabase } from './supabase';
import { isDoctor, getDoctorIdFromUser, canViewPatientFull } from './rbac';
import type { Patient, Doctor, Appointment, Invoice, InvoiceItem, Payment, Setting } from './supabase';
import { api, USE_MOCK } from './api';

const mockPatients: Patient[] = [
  {
    id: 'patient-1',
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '+1-555-0123',
    date_of_birth: '1985-03-15',
    address: '123 Main St, City, State 12345',
    emergency_contact: 'Jane Smith - +1-555-0124',
    medical_history: 'No known allergies. Previous surgery: Appendectomy (2018)',
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: 'patient-2',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1-555-0456',
    date_of_birth: '1990-07-22',
    address: '456 Oak Ave, Town, State 67890',
    emergency_contact: 'Mike Johnson - +1-555-0457',
    medical_history: 'Diabetes Type 2 (diagnosed 2020), Hypertension',
    created_at: '2024-01-16T14:30:00Z',
    updated_at: '2024-01-16T14:30:00Z'
  }
];

export const isSupabaseConfigured = () => {
  return true;
};

// Patient service
export const patientService = {
  async getAll(search?: string, user?: any) {
    if (!USE_MOCK) {
      return await api.patients.getAll(search);
    }
    
    let query = supabase.from('patients').select('*').order('created_at', { ascending: false });
    
    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
    }
    
    const { data, error } = await query;
    if (error) {
      console.warn('Supabase request failed, using mock data:', error);
      return search ? mockPatients.filter(p => 
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.email && p.email.toLowerCase().includes(search.toLowerCase())) ||
        (p.phone && p.phone.includes(search))
      ) : mockPatients;
    }
    
    let patients = data as Patient[];
    
    if (user && isDoctor(user)) {
      return patients.map(patient => ({
        ...patient,
        email: canViewPatientFull(user, patient.id) ? patient.email : undefined,
        phone: canViewPatientFull(user, patient.id) ? patient.phone : patient.phone?.substring(0, 4) + '****',
        address: canViewPatientFull(user, patient.id) ? patient.address : undefined,
        emergency_contact: canViewPatientFull(user, patient.id) ? patient.emergency_contact : undefined,
        medical_history: canViewPatientFull(user, patient.id) ? patient.medical_history : undefined,
      }));
    }
    
    return patients;
  },

  async getAllLimited(user?: any): Promise<Partial<Patient>[]> {
    const allPatients = await this.getAll(undefined, user);
    
    return allPatients.map((patient: any) => ({
      id: patient.id,
      name: patient.name,
      date_of_birth: patient.date_of_birth,
      phone: patient.phone?.substring(0, 4) + '****',
    }));
  },

  async getById(id: string) {
    if (!USE_MOCK) {
      return await api.patients.getById(id);
    }
    
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      const mockPatient = mockPatients.find(p => p.id === id);
      if (!mockPatient) throw error;
      return mockPatient;
    }
    return data as Patient;
  },

  async create(patient: Omit<Patient, 'id' | 'created_at' | 'updated_at'>) {
    if (!USE_MOCK) {
      return await api.patients.create(patient);
    }
    
    const { data, error } = await supabase
      .from('patients')
      .insert([{ ...patient, updated_at: new Date().toISOString() }])
      .select()
      .single();
    
    if (error) {
      const mockPatient: Patient = {
        id: `patient-${Date.now()}`,
        ...patient,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      mockPatients.push(mockPatient);
      return mockPatient;
    }
    return data as Patient;
  },

  async update(id: string, patient: Partial<Patient>) {
    if (!USE_MOCK) {
      return await api.patients.update(id, patient);
    }
    
    const { data, error } = await supabase
      .from('patients')
      .update({ ...patient, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      const mockIndex = mockPatients.findIndex(p => p.id === id);
      if (mockIndex === -1) throw error;
      mockPatients[mockIndex] = { ...mockPatients[mockIndex], ...patient, updated_at: new Date().toISOString() };
      return mockPatients[mockIndex];
    }
    return data as Patient;
  },

  async delete(id: string) {
    if (!USE_MOCK) {
      return await api.patients.delete(id);
    }
    
    const { error } = await supabase
      .from('patients')
      .delete()
      .eq('id', id);
    
    if (error) {
      const mockIndex = mockPatients.findIndex(p => p.id === id);
      if (mockIndex === -1) throw error;
      mockPatients.splice(mockIndex, 1);
      return;
    }
  }
};

// Doctor service
export const doctorService = {
  async getAll(search?: string) {
    let query = supabase.from('doctors').select('*').order('created_at', { ascending: false });
    
    if (search) {
      query = query.or(`name.ilike.%${search}%,specialization.ilike.%${search}%,department.ilike.%${search}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Doctor[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as Doctor;
  },

  async create(doctor: Omit<Doctor, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('doctors')
      .insert([{ ...doctor, updated_at: new Date().toISOString() }])
      .select()
      .single();
    
    if (error) throw error;
    return data as Doctor;
  },

  async uploadDocument(file: File, doctorId: string, docType: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${doctorId}/${docType}_${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('doctor-documents')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: urlData } = await supabase.storage
      .from('doctor-documents')
      .createSignedUrl(fileName, 31536000);

    if (!urlData) throw new Error('Failed to create signed URL');

    return urlData.signedUrl;
  },

  async update(id: string, doctor: Partial<Doctor>) {
    const { data, error } = await supabase
      .from('doctors')
      .update({ ...doctor, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as Doctor;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('doctors')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Appointment service
export const appointmentService = {
  async getAll(startDate?: string, endDate?: string, user?: any) {
    let query = supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .order('appointment_date', { ascending: true })
      .order('start_time', { ascending: true });
    
    if (startDate) {
      query = query.gte('appointment_date', startDate);
    }
    if (endDate) {
      query = query.lte('appointment_date', endDate);
    }
    
    if (user && isDoctor(user)) {
      const doctorId = getDoctorIdFromUser(user);
      if (doctorId) {
        query = query.eq('doctor_id', doctorId);
      }
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Appointment[];
  },

  async getByDoctor(doctorId: string) {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('doctor_id', doctorId)
      .order('appointment_date', { ascending: true })
      .order('start_time', { ascending: true });
    
    if (error) throw error;
    return data as Appointment[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as Appointment;
  },

  async create(appointment: Omit<Appointment, 'id' | 'created_at' | 'updated_at' | 'patient' | 'doctor'>) {
    const { data, error } = await supabase
      .from('appointments')
      .insert([{ ...appointment, updated_at: new Date().toISOString() }])
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .single();
    
    if (error) throw error;
    return data as Appointment;
  },

  async update(id: string, appointment: Partial<Appointment>) {
    const { data, error } = await supabase
      .from('appointments')
      .update({ ...appointment, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .single();
    
    if (error) throw error;
    return data as Appointment;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('appointments')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Invoice service
export const invoiceService = {
  async getAll() {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as Invoice[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as Invoice;
  },

  async getByReference(referenceId: string, referenceType: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('reference_id', referenceId)
      .eq('reference_type', referenceType)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as Invoice;
  },

  async create(invoice: Omit<Invoice, 'id' | 'created_at' | 'updated_at' | 'patient' | 'items' | 'payments'>) {
    // Generate invoice number
    const invoiceNumber = `INV-${Date.now()}`;
    
    const { data, error } = await supabase
      .from('invoices')
      .insert([{ 
        ...invoice, 
        invoice_number: invoiceNumber,
        updated_at: new Date().toISOString() 
      }])
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .single();
    
    if (error) throw error;
    return data as Invoice;
  },

  async update(id: string, invoice: Partial<Invoice>) {
    const { data, error } = await supabase
      .from('invoices')
      .update({ ...invoice, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .single();
    
    if (error) throw error;
    return data as Invoice;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async addItem(invoiceId: string, item: Omit<InvoiceItem, 'id' | 'invoice_id' | 'total' | 'created_at'>) {
    const { data, error } = await supabase
      .from('invoice_items')
      .insert([{ ...item, invoice_id: invoiceId }])
      .select()
      .single();
    
    if (error) throw error;
    return data as InvoiceItem;
  },

  async updateItem(id: string, item: Partial<InvoiceItem>) {
    const { data, error } = await supabase
      .from('invoice_items')
      .update(item)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as InvoiceItem;
  },

  async deleteItem(id: string) {
    const { error } = await supabase
      .from('invoice_items')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Payment service
export const paymentService = {
  async create(payment: Omit<Payment, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('payments')
      .insert([payment])
      .select()
      .single();
    
    if (error) throw error;
    return data as Payment;
  }
};

// Settings service
export const settingsService = {
  async get(key: string) {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', key)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data?.value;
  },

  async set(key: string, value: any) {
    const { data, error } = await supabase
      .from('settings')
      .upsert([{ 
        key, 
        value, 
        updated_at: new Date().toISOString() 
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data as Setting;
  },

  async getAll() {
    const { data, error } = await supabase
      .from('settings')
      .select('*');
    
    if (error) throw error;
    return data as Setting[];
  }
};

// Reports service
export const reportsService = {
  async getStats(startDate: string, endDate: string) {
    const endDateTime = `${endDate} 23:59:59`;
    
    // Get appointment stats
    const { data: appointmentStats, error: appointmentError } = await supabase
      .from('appointments')
      .select('status')
      .gte('appointment_date', startDate)
      .lte('appointment_date', endDate);
    
    if (appointmentError) throw appointmentError;

    const { data: payments, error: paymentsError } = await supabase
      .from('payments')
      .select('amount, payment_date, payment_method')
      .gte('payment_date', startDate)
      .lte('payment_date', endDateTime);
    
    if (paymentsError) throw paymentsError;

    const { data: invoices, error: invoicesError } = await supabase
      .from('invoices')
      .select('id, total, status, issue_date, reference_type, payments(amount)')
      .gte('issue_date', startDate)
      .lte('issue_date', endDate);
    
    if (invoicesError) throw invoicesError;

    // Calculate appointment counts by status
    const appointmentCounts = appointmentStats.reduce((acc, apt) => {
      acc[apt.status] = (acc[apt.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate collected revenue (sum of actual payments received)
    const collectedRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

    // Calculate total invoiced amount
    const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);

    // Calculate pending revenue (outstanding amounts on sent/partial/overdue invoices)
    const pendingRevenue = invoices
      .filter(inv => ['sent', 'partial', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => {
        const paid = inv.payments?.reduce((s: number, p: any) => s + (p.amount || 0), 0) || 0;
        const outstanding = Math.max(0, inv.total - paid);
        return sum + outstanding;
      }, 0);

    // Calculate overdue revenue
    const overdueRevenue = invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => {
        const paid = inv.payments?.reduce((s: number, p: any) => s + (p.amount || 0), 0) || 0;
        const outstanding = Math.max(0, inv.total - paid);
        return sum + outstanding;
      }, 0);

    const revenueByDepartment = invoices.reduce((acc, inv) => {
      const dept = inv.reference_type || 'other';
      if (!acc[dept]) acc[dept] = 0;
      acc[dept] += inv.total || 0;
      return acc;
    }, {} as Record<string, number>);

    const paymentsByMethod = payments.reduce((acc, p) => {
      const method = p.payment_method || 'cash';
      if (!acc[method]) acc[method] = 0;
      acc[method] += p.amount || 0;
      return acc;
    }, {} as Record<string, number>);

    // Calculate daily revenue for charts
    const dailyRevenue = payments.reduce((acc, p) => {
      const date = p.payment_date.split('T')[0];
      if (!acc[date]) acc[date] = 0;
      acc[date] += p.amount || 0;
      return acc;
    }, {} as Record<string, number>);

    return {
      appointments: appointmentCounts,
      revenue: {
        collected: collectedRevenue,
        invoiced: totalInvoiced,
        pending: pendingRevenue,
        overdue: overdueRevenue,
        invoiceCount: invoices.length,
        byDepartment: revenueByDepartment,
        byPaymentMethod: paymentsByMethod,
        dailySeries: Object.entries(dailyRevenue).map(([date, amount]) => ({ date, amount })).sort((a, b) => a.date.localeCompare(b.date))
      }
    };
  }
};
export const serviceCatalogService = {
  async getAll(type?: string) {
    let query = supabase
      .from('service_catalog')
      .select('*')
      .eq('active', true)
      .order('name');
    
    if (type) {
      query = query.eq('type', type);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('service_catalog')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(service: any) {
    const { data, error } = await supabase
      .from('service_catalog')
      .insert(service)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, service: any) {
    const { data, error } = await supabase
      .from('service_catalog')
      .update({ ...service, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const referralContractService = {
  async getAll() {
    const { data, error } = await supabase
      .from('referral_contracts')
      .select('*, service:service_catalog(*)')
      .eq('active', true)
      .order('referrer_name');
    
    if (error) throw error;
    return data;
  },

  async create(contract: any) {
    const { data, error } = await supabase
      .from('referral_contracts')
      .insert(contract)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, contract: any) {
    const { data, error } = await supabase
      .from('referral_contracts')
      .update(contract)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async accrueCommission(accrual: any) {
    const { data, error } = await supabase
      .from('referral_accruals')
      .insert(accrual)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getPayoutReport(startDate?: string, endDate?: string, referrer?: string) {
    let query = supabase
      .from('referral_accruals')
      .select('*, contract:referral_contracts(*)');
    
    if (startDate) query = query.gte('created_at', startDate);
    if (endDate) query = query.lte('created_at', endDate);
    if (referrer) query = query.eq('referrer_name', referrer);
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },
};

export const opdPaymentService = {
  async createConsultationInvoice(appointmentId: string, patientId: string, serviceId: string, amount: number) {
    const invoiceNumber = `INV-${Date.now()}`;
    const today = new Date().toISOString().split('T')[0];
    
    const invoice = await invoiceService.create({
      patient_id: patientId,
      invoice_number: invoiceNumber,
      issue_date: today,
      reference_id: appointmentId,
      reference_type: 'consultation',
      status: 'draft',
      subtotal: amount,
      tax_rate: 0,
      tax_amount: 0,
      total: amount,
      amount_paid: 0,
      balance_due: amount,
    });

    await invoiceService.addItem(invoice.id, {
      description: 'Consultation Fee',
      quantity: 1,
      unit_price: amount,
    });

    return invoice;
  },

  async createInvestigationInvoice(orderId: string, orderType: 'lab' | 'radiology', patientId: string, items: Array<{ description: string; price: number }>) {
    const total = items.reduce((sum, item) => sum + item.price, 0);
    const invoiceNumber = `INV-${Date.now()}`;
    const today = new Date().toISOString().split('T')[0];

    const invoice = await invoiceService.create({
      patient_id: patientId,
      invoice_number: invoiceNumber,
      issue_date: today,
      reference_id: orderId,
      reference_type: orderType,
      status: 'draft',
      subtotal: total,
      tax_rate: 0,
      tax_amount: 0,
      total,
      amount_paid: 0,
      balance_due: total,
    });

    for (const item of items) {
      await invoiceService.addItem(invoice.id, {
        description: item.description,
        quantity: 1,
        unit_price: item.price,
      });
    }

    return invoice;
  },

  async getDues(patientId?: string, startDate?: string, endDate?: string) {
    let query = supabase
      .from('invoices')
      .select('*, patient:patients(*), items:invoice_items(*), payments:payments(*)')
      .in('status', ['sent', 'overdue']);
    
    if (patientId) query = query.eq('patient_id', patientId);
    if (startDate) query = query.gte('issue_date', startDate);
    if (endDate) query = query.lte('issue_date', endDate);
    
    const { data, error } = await query;
    if (error) throw error;

    return data.map(invoice => {
      const totalPaid = invoice.payments?.reduce((sum: number, p: any) => sum + p.amount, 0) || 0;
      return {
        ...invoice,
        balance: invoice.total - totalPaid,
      };
    }).filter(inv => inv.balance > 0);
  },
};
